import { createContext, useContext, useEffect } from "react";

import { Site } from "../../model/site";

/**
 * Lets editor screens show unsaved changes in the live preview: a screen publishes a
 * draft version of the site here, and clears it (undefined) when it's done. Nothing is
 * saved - only the preview reads it.
 */
export type PreviewSiteContextType = {
  setPreviewSite: (site?: Site) => void;
  /** Shows a loading state over the preview (e.g. while a template's content loads). */
  setPreviewLoading: (loading: boolean) => void;
  /** Shows a message in place of the Keepo (e.g. nothing picked yet to preview); undefined clears it. */
  setPreviewHint: (hint?: PreviewHint) => void;
  /** Shows a browser address bar on top of the Keepo, with this address (e.g. while choosing it). */
  setPreviewAddress: (address?: string) => void;
};

export type PreviewHint = { title: string; text: string };

export const PreviewSiteContext = createContext<PreviewSiteContextType>({
  setPreviewSite: () => undefined,
  setPreviewLoading: () => undefined,
  setPreviewHint: () => undefined,
  setPreviewAddress: () => undefined,
});

export const usePreviewSite = () => useContext(PreviewSiteContext);

/** Shows `draftSite` in the live preview while the calling screen is mounted. */
export const useDraftSitePreview = (draftSite?: Site) => {
  const { setPreviewSite } = usePreviewSite();

  useEffect(() => {
    if (draftSite) {
      setPreviewSite(draftSite);
    }
  }, [draftSite, setPreviewSite]);

  useEffect(() => () => setPreviewSite(undefined), [setPreviewSite]);
};
